import { formatNumberAbbr } from './number'

export type Currency = 'EUR' | 'GBP' | 'USD' | 'ZAR'

export const CURRENCY_DECIMAL_PLACES = 2

export type ForExRateFn = (base: Currency, counter: Currency) => number

export function makeForExRateFn(
  fxRates: Array<{ currencyPair: string; spotRate: number }>
): ForExRateFn {
  const rateMap = new Map()
  if (Array.isArray(fxRates)) {
    fxRates.forEach(({ currencyPair, spotRate }) => {
      rateMap.set(`${currencyPair}`, spotRate)
    })
  }

  return (base, counter) => {
    if (base === counter) {
      return 1
    }
    if (rateMap.has(`${base}/${counter}`)) {
      return rateMap.get(`${base}/${counter}`)
    }
    if (rateMap.has(`${counter}/${base}`)) {
      return 1 / rateMap.get(`${counter}/${base}`)
    }
    throw Error(`Could not find forex rate for ${base} to ${counter}.`)
  }
}

export function currencyName(currency: Currency): string {
  switch (currency) {
    case 'USD':
      return 'US Dollar'
    case 'GBP':
      return 'British Pound'
    case 'EUR':
      return 'Euro'
    case 'ZAR':
      return 'SA Rand'
    default:
      return ''
  }
}

export function currencyCountryCode(currency: Currency): string {
  switch (currency) {
    case 'USD':
      return 'us'
    case 'GBP':
      return 'gb'
    case 'EUR':
      return 'eu'
    case 'ZAR':
      return 'za'
    default:
      return ''
  }
}

export function currencySymbol(currency: Currency): string {
  switch (currency) {
    case 'USD':
      return '$'
    case 'GBP':
      return '£'
    case 'EUR':
      return '€'
    case 'ZAR':
      return 'R'
    default:
      return currency
  }
}

export function currencyDecimalPlaces(currency: Currency): number {
  switch (currency) {
    default:
      return 2
  }
}

// TODO Make locale aware.
export function formatCurrency(
  value: number,
  currency: Currency,
  abbreviatedDecimalPlaces = 1
): string {
  const isNegative = value < 0
  const valueWithCurrency =
    currencySymbol(currency) + formatNumberAbbr(value, abbreviatedDecimalPlaces)
  const formattedValue = isNegative ? `-${valueWithCurrency.replace('-', '')}` : valueWithCurrency
  return formattedValue
}

export function formatCurrencyFull(
  value: number,
  currency: Currency,
  options?: Record<string, unknown>,
  isScaled = false
): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '–'
  const isNegative = value < 0
  const valueWithCurrency = value
    ?.toLocaleString(undefined, {
      ...options,
      currency,
      style: 'currency'
    })
    .replace(/US\$/, `${currencySymbol('USD')}`)
    .replace(/ZAR/, isScaled ? 'ZAc' : 'R')
    .replace(/\s/, '')
  const formattedValue = isNegative ? `-${valueWithCurrency.replace('-', '')}` : valueWithCurrency
  return formattedValue
}
